import React, { useState, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getTranslatedProductName, getTranslatedCategory } from '../../utils/translationHelpers';
import { 
  User, 
  MapPin, 
  Package, 
  ShoppingCart, 
  Search, 
  Filter, 
  Star,
  CheckCircle,
  AlertCircle,
  Flower
} from 'lucide-react';
import {
  OptimizedFormContainer,
  OptimizedFormSection,
  OptimizedFormGrid,
  OptimizedProductGrid,
  OptimizedProductCard,
  OptimizedOrderSummary,
  OptimizedSubmitButton,
  SearchAndFilterContainer,
  SearchInput,
  FilterSelect,
  CategoryBadge,
  SectionTitle,
  FieldContainer,
  QuantityControl,
  LoadingSpinner,
  GlobalOrderSummary
} from './shared/OptimizedFormComponents';
import { PreviewOrderButton } from './shared/PreviewOrderButton';
import {
  CustomerInformationSection,
  OrderInformationSection,
  SharedOrderFormData,
  getMockCustomerData
} from './shared/SharedFormSections';
import { useCustomerData } from './shared/CustomerDataService';
import { useGlobalOrderData, OrderItem } from '../../hooks/useGlobalOrderData';
import { FormStatusIndicator } from './shared/FormStatusIndicator';
import { BackToTopButton } from './shared/BackToTopButton';
import { ThemeColors, Spacing, Typography } from '../../styles/ThemeStyles';

interface OrderItem {
  id: string;
  packetDescription: string;
  code: string;
  quantity: number;
  price: number;
  category: 'flower';
}

interface OrderFormData extends SharedOrderFormData {
  items: OrderItem[];
}

// Product data based on CSV analysis - categorized by price point only, letters (A), (P), (W), (B) are part of product names
const flowerProducts = [
  // $2.49 Flower
  { id: '1', name: '(A) Alyssum Carpet of Snow', code: '143431', price: 2.49, category: 'flower' as const },
  { id: '2', name: '(A) Aster Early Charm', code: '143432', price: 2.49, category: 'flower' as const },
  { id: '3', name: '(A) Aster Powder Puffs', code: '143433', price: 2.49, category: 'flower' as const },
  { id: '4', name: '(A) Calendula Pacific Beauty Mix', code: '143438', price: 2.49, category: 'flower' as const },
  { id: '5', name: '(A) Cosmos Early Sensation Mix', code: '143444', price: 2.49, category: 'flower' as const },
  { id: '6', name: '(A) Forget-Me-Not Cynoglossum', code: '143447', price: 2.49, category: 'flower' as const },
  { id: '7', name: '(A) Lavatera Silvercup', code: '143450', price: 2.49, category: 'flower' as const },
  { id: '8', name: '(A) Marigold Crackerjack', code: '143453', price: 2.49, category: 'flower' as const },
  { id: '9', name: '(A) Phlox Dwarf Beauty Mixture', code: '143475', price: 2.49, category: 'flower' as const },
  { id: '10', name: '(A) Poppy Falling In Love', code: '143477', price: 2.49, category: 'flower' as const },
  { id: '11', name: '(A) Snapdragon Mini Snap Mix', code: '143479', price: 2.49, category: 'flower' as const },
  { id: '12', name: '(A) Stocks Night Scented (Matthiola)', code: '143480', price: 2.49, category: 'flower' as const },
  { id: '13', name: '(A) Bean Flowering Vine Scarlet Runner', code: '143659', price: 2.49, category: 'flower' as const },
  { id: '14', name: '(P) Columbine Long Spurred Mixed', code: '143527', price: 2.49, category: 'flower' as const },
  { id: '15', name: '(A) Cosmos Candy Stripe Kids', code: '143442', price: 2.49, category: 'flower' as const },
  { id: '16', name: '(P) Lupins Russell Mix', code: '143535', price: 2.49, category: 'flower' as const },
  { id: '17', name: '(A) Morning Glory Early Call Mixed', code: '143460', price: 2.49, category: 'flower' as const },
  { id: '18', name: '(A) Morning Glory Heavenly Blue', code: '143462', price: 2.49, category: 'flower' as const },
  { id: '19', name: '(A) Morning Glory Mix', code: '143463', price: 2.49, category: 'flower' as const },
  { id: '20', name: '(A) Nasturtium Alaska Mixed', code: '143464', price: 2.49, category: 'flower' as const },
  { id: '21', name: '(A) Nasturtium Dwarf Double Cherry Rose', code: '143465', price: 2.49, category: 'flower' as const },
  { id: '22', name: '(A) Nasturtium Empress of India', code: '143466', price: 2.49, category: 'flower' as const },
  { id: '23', name: '(A) Nasturtium Jewel Mixed', code: '143467', price: 2.49, category: 'flower' as const },
  { id: '24', name: '(A) Nasturtium Tall Climbing Mix', code: '143469', price: 2.49, category: 'flower' as const },
  { id: '25', name: '(A) Sunflower Autumn Beauty', code: '143481', price: 2.49, category: 'flower' as const },
  { id: '26', name: '(A) Sunflower Evening Sun', code: '143482', price: 2.49, category: 'flower' as const },
  { id: '27', name: '(A) Sunflower Golden Hedge', code: '143483', price: 2.49, category: 'flower' as const },
  { id: '28', name: '(A) Sunflower Henri Wilde', code: '143484', price: 2.49, category: 'flower' as const },
  { id: '29', name: '(A) Sunflower Large Seeded Tall', code: '143485', price: 2.49, category: 'flower' as const },
  { id: '30', name: '(A) Sunflower Lemon Queen', code: '143486', price: 2.49, category: 'flower' as const },
  { id: '31', name: '(A) Sunflower Red Sun', code: '143489', price: 2.49, category: 'flower' as const },
  { id: '32', name: '(A) Sunflower Sunspot', code: '143490', price: 2.49, category: 'flower' as const },
  { id: '33', name: '(A) Sunflower Taiyo', code: '143491', price: 2.49, category: 'flower' as const },
  { id: '34', name: '(A) Sunflower Velvet Queen', code: '143493', price: 2.49, category: 'flower' as const },
  { id: '35', name: '(P) Sweet Pea Everlasting', code: '143495', price: 2.49, category: 'flower' as const },
  { id: '36', name: '(A) Sweet Pea Little Sweetheart Mix', code: '143496', price: 2.49, category: 'flower' as const },
  { id: '37', name: '(A) Sweet Pea Spencer Giant Mixture', code: '143498', price: 2.49, category: 'flower' as const },
  { id: '38', name: '(A) Sweet Pea Sugar and Spice', code: '143500', price: 2.49, category: 'flower' as const },
  { id: '39', name: '(B) Sweet William Mixed Colors', code: '143501', price: 2.49, category: 'flower' as const },
  { id: '40', name: '(A) Zinnia Berry Tart Mix', code: '143507', price: 2.49, category: 'flower' as const },
  { id: '41', name: '(A) Zinnia Candy Cane', code: '143509', price: 2.49, category: 'flower' as const },
  { id: '42', name: '(A) Zinnia Giant Cactus Flowered Mixture', code: '143510', price: 2.49, category: 'flower' as const },

  // $2.79 Flower
  { id: '43', name: '(P) Alyssum Saxatile', code: '143523', price: 2.79, category: 'flower' as const },
  { id: '44', name: '(P) Aubretia Audrey Red Purple Mix', code: '143524', price: 2.79, category: 'flower' as const },
  { id: '45', name: '(A) Baby\'s Breath Gypsophila Elegans', code: '143434', price: 2.79, category: 'flower' as const },
  { id: '46', name: '(A) Bachelor Buttons Fantastic Mix', code: '143435', price: 2.79, category: 'flower' as const },
  { id: '47', name: '(A) Black Eyed Susan Vine Thunbergia', code: '143436', price: 2.79, category: 'flower' as const },
  { id: '48', name: '(P) Canary Bird Vine', code: '143439', price: 2.79, category: 'flower' as const },
  { id: '49', name: '(A) Chrysanthemum Bright Eye', code: '143440', price: 2.79, category: 'flower' as const },
  { id: '50', name: '(P) Columbine Blue Star', code: '143526', price: 2.79, category: 'flower' as const },
  { id: '51', name: '(A) Coleus Rainbow Mix', code: '143441', price: 2.79, category: 'flower' as const },
  { id: '52', name: '(A) Cosmos Double Click', code: '143443', price: 2.79, category: 'flower' as const },
  { id: '53', name: '(A) Dianthus Maiden Pink', code: '143531', price: 2.79, category: 'flower' as const },
  { id: '54', name: '(P) Delphinium Pacific Giants', code: '143529', price: 2.79, category: 'flower' as const },
  { id: '55', name: '(A) Flower Garden Old Fashioned', code: '143446', price: 2.79, category: 'flower' as const },
  { id: '56', name: '(A) Four O\'Clock Mix', code: '143448', price: 2.79, category: 'flower' as const },
  { id: '57', name: '(A) Gaillardia Grandiflora Mix', code: '143532', price: 2.79, category: 'flower' as const },
  { id: '58', name: '(A) Lobelia Crystal Palace', code: '143452', price: 2.79, category: 'flower' as const },
  { id: '59', name: '(P) Lupins Russell Mix', code: '143535', price: 2.79, category: 'flower' as const },
  { id: '60', name: '(P) Malva Mystic Mixture', code: '143536', price: 2.79, category: 'flower' as const },
  { id: '61', name: '(P) Malva Zebrina', code: '143537', price: 2.79, category: 'flower' as const },
  { id: '62', name: '(A) Marigold French Double Dwarf', code: '143455', price: 2.79, category: 'flower' as const },
  { id: '63', name: '(A) Marigold Hero Mix', code: '143456', price: 2.79, category: 'flower' as const },
  { id: '64', name: '(A) Milkweed Butterfly', code: '143457', price: 2.79, category: 'flower' as const },
  { id: '65', name: '(A) Nemophilla Penny Black', code: '143470', price: 2.79, category: 'flower' as const },
  { id: '66', name: '(P) Ornamental Grass Bunny Tails', code: '143472', price: 2.79, category: 'flower' as const },
  { id: '67', name: '(B) Pansy Show Swiss Giant', code: '143473', price: 2.79, category: 'flower' as const },
  { id: '68', name: '(P) Poppy Iceland Mixed Colours', code: '143539', price: 2.79, category: 'flower' as const },
  { id: '69', name: '(P) Rudbeckia Goldsturm', code: '143540', price: 2.79, category: 'flower' as const },
  { id: '70', name: '(P) Scabiosa Shepherds', code: '143541', price: 2.79, category: 'flower' as const },
  { id: '71', name: '(P) Shasta Daisy Alaska', code: '143542', price: 2.79, category: 'flower' as const },
  { id: '72', name: '(P) Soapwort Rock', code: '143543', price: 2.79, category: 'flower' as const },
  { id: '73', name: '(A) Sunflower Munchkin Kids', code: '143487', price: 2.79, category: 'flower' as const },
  { id: '74', name: '(A) Sunflower Vanilla Ice Cream', code: '143492', price: 2.79, category: 'flower' as const },
  { id: '75', name: '(A) Sweet Pea Cupani', code: '143494', price: 2.79, category: 'flower' as const },
  { id: '76', name: '(A) Sweet Pea Ocean Foam', code: '143497', price: 2.79, category: 'flower' as const },
  { id: '77', name: '(W) Wildflower All Purpose Mixture', code: '143502', price: 2.79, category: 'flower' as const },
  { id: '78', name: '(W) Wildflower Black Eyed Susan', code: '143544', price: 2.79, category: 'flower' as const },
  { id: '79', name: '(A) Zinnia California Dble Giant', code: '143508', price: 2.79, category: 'flower' as const },

  // $3.49 Flower
  { id: '80', name: '(A) Calendula Neon', code: '143437', price: 3.49, category: 'flower' as const },
  { id: '81', name: '(P) Cupids Dart', code: '143528', price: 3.49, category: 'flower' as const },
  { id: '82', name: '(A) Cypress Vine White', code: '143445', price: 3.49, category: 'flower' as const },
  { id: '83', name: '(P) Dianthus Arctic Fire', code: '143530', price: 3.49, category: 'flower' as const },
  { id: '84', name: '(P) Geum Red Dragon', code: '143533', price: 3.49, category: 'flower' as const },
  { id: '85', name: '(A) Impatiens Tropical Fizz Hybrid', code: '143449', price: 3.49, category: 'flower' as const },
  { id: '86', name: '(A) Lavatera Twins Hot Pink', code: '143451', price: 3.49, category: 'flower' as const },
  { id: '87', name: '(A) Marigold Eskimo', code: '143454', price: 3.49, category: 'flower' as const },
  { id: '88', name: '(A) Moonflower', code: '143458', price: 3.49, category: 'flower' as const },
  { id: '89', name: '(A) Morning Glory Flying Saucers', code: '143461', price: 3.49, category: 'flower' as const },
  { id: '90', name: '(A) Nasturtium Night & Day', code: '143468', price: 3.49, category: 'flower' as const },
  { id: '91', name: '(A) Orn Corn Japan Stars & Stripes', code: '143471', price: 3.49, category: 'flower' as const },
  { id: '92', name: '(A) Pampas Grass Pink OG', code: '143538', price: 3.49, category: 'flower' as const },
  { id: '93', name: '(A) Petunia Dwarf Bedding Mix', code: '143474', price: 3.49, category: 'flower' as const },
  { id: '94', name: '(A) Poppy Flanders Field', code: '143478', price: 3.49, category: 'flower' as const },
  { id: '95', name: '(A) Sunflower Paquito', code: '143488', price: 3.49, category: 'flower' as const },
  { id: '96', name: '(A) Sweet Pea Streamers Mix', code: '143499', price: 3.49, category: 'flower' as const },

  // $3.99 Flower Value Size
  { id: '97', name: '(A) Marigold Crackerjack', code: '143511', price: 3.99, category: 'flower' as const },
  { id: '98', name: '(A) Morning Glory Mix', code: '143512', price: 3.99, category: 'flower' as const },
  { id: '99', name: '(A) Nasturtium Jewel mixed', code: '143513', price: 3.99, category: 'flower' as const },
  { id: '100', name: '(A) Old Fashioned Flower Garden', code: '143514', price: 3.99, category: 'flower' as const },
  { id: '101', name: '(A) Sweet Pea Knee Hi', code: '143515', price: 3.99, category: 'flower' as const },
  { id: '102', name: '(A) Sweet Pea Mix Starry Night', code: '143516', price: 3.99, category: 'flower' as const },
  { id: '103', name: '(A) Sweet Pea Spencer Giant Mixture', code: '143518', price: 3.99, category: 'flower' as const },
  { id: '104', name: '(W) Wildflower All Purpose Mixture', code: '143519', price: 3.99, category: 'flower' as const },
  { id: '105', name: '(W) Wildflower Bird & Butterfly Garden', code: '143520', price: 3.99, category: 'flower' as const },
  { id: '106', name: '(W) Wildflower Hummingbird Gdn', code: '143521', price: 3.99, category: 'flower' as const },
  { id: '107', name: '(A) Zinnia Tetra Flowered', code: '143522', price: 3.99, category: 'flower' as const },
];

const FormValidation = styled.div`
  margin-bottom: ${Spacing.lg};
`;

const ProductCategorySection = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Spacing.lg};
  padding: ${Spacing.md};
  background: ${ThemeColors.primary.light};
  border-radius: ${Spacing.sm};
  
  .category-info {
    display: flex;
    align-items: center;
    gap: ${Spacing.md};
    
    .category-title {
      font-size: ${Typography.fontSize.lg};
      font-weight: ${Typography.fontWeight.bold};
      color: ${ThemeColors.text.primary};
    }
    
    .category-count {
      background: ${ThemeColors.primary.main};
      color: white;
      padding: ${Spacing.xs} ${Spacing.sm};
      border-radius: ${Spacing.sm};
      font-size: ${Typography.fontSize.sm};
      font-weight: ${Typography.fontWeight.semibold};
    }
  }
  
  .category-price {
    font-size: ${Typography.fontSize.xl};
    font-weight: ${Typography.fontWeight.bold};
    color: ${ThemeColors.primary.main};
  }
`;

export const OptimizedFlowerOrderForm: React.FC<{ storeId?: string; onShowPreview?: () => void }> = ({ storeId, onShowPreview }) => {
  const { t } = useTranslation();
  
  // Use global order data with cache functionality
  const { 
    orderData, 
    updateOrderData, 
    updateFlowerItems,
    submitOrder,
    isLoading: globalLoading,
    error: globalError,
    getFormStatus,
    autoSave,
    clearDraft
  } = useGlobalOrderData();
  
  // Fetch customer data from Firebase
  const { customerData, loading: customerLoading, error: customerError } = useCustomerData(storeId || '');
  
  const [selectedItems, setSelectedItems] = useState<{ [key: string]: number }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const prevItemsRef = useRef<string>('');
  
  // Get form status for the status indicator
  const formStatus = getFormStatus('flower');
  
  // Reset initialization when storeId changes
  useEffect(() => {
    setIsInitialized(false);
    setSelectedItems({});
    prevItemsRef.current = '';
  }, [storeId]);

  // Sync selected items with global order data (only on initial load, once per storeId)
  // Also reset when items become empty (after finalization)
  useEffect(() => {
    if (orderData.flowerItems === undefined) {
      prevItemsRef.current = '';
      return;
    }
    
    // Create a stable string representation for comparison
    const currentItemsKey = orderData.flowerItems.length > 0
      ? orderData.flowerItems
          .map(item => `${item.id}:${item.quantity}`)
          .sort()
          .join('|')
      : '';
    
    // Only process if items actually changed (prevents infinite loops)
    if (currentItemsKey === prevItemsRef.current) return;
    
    const itemsLength = orderData.flowerItems.length;
    
    if (itemsLength === 0 && isInitialized) {
      // Reset if items were cleared (e.g., after finalization)
      setSelectedItems({});
      setIsInitialized(false); // Allow re-initialization when new items are added
      prevItemsRef.current = currentItemsKey;
      console.log('🔄 Flower form reset - items cleared');
    } else if (itemsLength > 0 && !isInitialized) {
      // Initialize with items from StoreOrders
      const itemsMap: { [key: string]: number } = {};
      orderData.flowerItems.forEach(item => {
        // Use exact quantity from StoreOrders - no transformation
        itemsMap[item.id] = item.quantity || 0;
      });
      setSelectedItems(itemsMap);
      setIsInitialized(true);
      prevItemsRef.current = currentItemsKey;
      console.log('✅ Flower form initialized with', itemsLength, 'items');
    } else if (itemsLength === 0 && !isInitialized) {
      // Items are empty and not initialized - just mark as processed to prevent re-running
      prevItemsRef.current = currentItemsKey;
    }
    // Only depend on length and isInitialized to prevent infinite loops
    // The ref comparison inside prevents unnecessary state updates when array reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData.flowerItems?.length, isInitialized]);

  const handleInputChange = (field: keyof SharedOrderFormData, value: string | boolean) => {
    updateOrderData({ [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Trigger auto-save
    autoSave();
  };

  const handleQuantityChange = (productId: string, quantity: number) => {
    const newSelectedItems = {
      ...selectedItems,
      [productId]: Math.max(0, quantity)
    };
    setSelectedItems(newSelectedItems);
    
    // Update global order data
    const orderItems: OrderItem[] = Object.entries(newSelectedItems)
      .filter(([_, qty]) => qty > 0)
      .map(([id, qty]) => {
        const product = flowerProducts.find(p => p.id === id);
        return {
          id,
          packetDescription: product?.name || '',
          code: product?.code || '',
          quantity: qty,
          price: product?.price || 0,
          category: 'flower' as const
        };
      });
    
    updateFlowerItems(orderItems);
    // Trigger auto-save
    autoSave();
  };


  // Filter products based on search and category
  const filteredProducts = useMemo(() => {
    return flowerProducts.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.code.includes(searchTerm);
      const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  // Group products by price
  const productsByPrice = useMemo(() => {
    const grouped: { [key: string]: typeof flowerProducts } = {};
    filteredProducts.forEach(product => {
      const priceKey = `$${product.price}`;
      if (!grouped[priceKey]) {
        grouped[priceKey] = [];
      }
      grouped[priceKey].push(product);
    });
    return grouped;
  }, [filteredProducts]);

  const handleSubmit = async () => {
    try {
      await submitOrder();
      // Handle success
    } catch (error) {
      console.error('Order submission failed:', error);
    }
  };

  const totalItems = Object.values(selectedItems).reduce((sum, qty) => sum + qty, 0);
  const totalPrice = Object.entries(selectedItems).reduce((sum, [id, qty]) => {
    const product = flowerProducts.find(p => p.id === id);
    return sum + (product?.price || 0) * qty;
  }, 0);

  if (globalLoading || customerLoading) {
    return <LoadingSpinner />;
  }

  return (
    <OptimizedFormContainer>
      {/* Form Status Indicator */}
      <FormStatusIndicator
        formType="flower"
        status={formStatus}
        onReset={clearDraft}
      />
      
      <OptimizedFormSection>
          <SectionTitle 
            icon={Flower} 
          title={t('forms.flower.title')}
          />
          
          <SearchAndFilterContainer>
            <SearchInput>
              <Search className="search-icon" size={20} />
              <input
                className="search-field"
                type="text"
            placeholder={t('forms.searchProducts')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchInput>
            
            <FilterSelect>
              <select
                className="filter-field"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
            <option value="all">{t('forms.allCategories')}</option>
            <option value="flower">{t('categories.flower')}</option>
              </select>
            </FilterSelect>
          </SearchAndFilterContainer>

        {Object.entries(productsByPrice)
          .sort(([a], [b]) => parseFloat(a.substring(1)) - parseFloat(b.substring(1)))
          .map(([price, products]) => (
            <ProductCategorySection key={price}>
                <CategoryHeader>
                  <div className="category-info">
                  <h3 className="category-title">{price} {t('categories.flower')}</h3>
                  <span className="category-count">{products.length} {products.length === 1 ? t('common.item') : t('common.items')}</span>
                    </div>
                <div className="category-price">{price}</div>
                </CategoryHeader>
                
                <OptimizedProductGrid>
                {products.map(product => (
                      <OptimizedProductCard 
                        key={product.id} 
                    isSelected={selectedItems[product.id] > 0}
                  >
                        <div className="product-header">
                      <h4 className="product-title">{getTranslatedProductName(product.name, t)}</h4>
                      <div className="product-price">{product.price}</div>
                        </div>
                        
                        <div className="product-code">{t('pdf.code')}: {product.code}</div>
                    <CategoryBadge>{getTranslatedCategory(product.category, t)}</CategoryBadge>
                    
                    <QuantityControl
                      value={selectedItems[product.id] || 0}
                      onChange={(value) => handleQuantityChange(product.id, value)}
                      min={0}
                      max={999}
                    />
                      </OptimizedProductCard>
                ))}
                </OptimizedProductGrid>
              </ProductCategorySection>
          ))}

        <GlobalOrderSummary />

        {/* Preview Order Button with Validation */}
        <PreviewOrderButton 
          onPreview={() => {
            if (onShowPreview) {
              onShowPreview();
            } else {
              console.log('Preview functionality not available');
            }
          }}
        />

        {globalError && (
          <div style={{ color: ThemeColors.error.main, marginTop: Spacing.md }}>
            {globalError}
        </div>
        )}
      </OptimizedFormSection>
      
      {/* Back to Top Button */}
      <BackToTopButton scrollThreshold={300} />
    </OptimizedFormContainer>
  );
};